import { useEffect, useState } from "react";
import { api, formatDuration } from "@/lib/api";
import { useSettings, imgSrc } from "@/lib/settings";
import { Reveal, Overline } from "./Reveal";
import { ArrowUpRight } from "lucide-react";

const CATEGORY_TITLES = {
  Cils: "Un regard de biche",
  Sourcils: "Des sourcils dessinés",
};

export function bookService(id) {
  window.dispatchEvent(new CustomEvent("preselect-service", { detail: id }));
  document.querySelector("#reservation")?.scrollIntoView({ behavior: "smooth" });
}

export default function Services() {
  const [categories, setCategories] = useState([]);
  const { settings } = useSettings();
  const categoryImages = settings.images.categories || {};

  useEffect(() => {
    api.get("/services")
      .then((res) => setCategories(res.data.categories.filter((c) => c.name !== "Tatouage")))
      .catch(() => {});
  }, []);

  return (
    <section id="prestations" className="bg-[var(--brand-section)] py-28 lg:py-40" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Overline className="mb-6">Nos prestations</Overline>
            <h2 className="font-display max-w-xl text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--brand-dark)]">
              L'art du détail, <span className="italic font-light text-[var(--brand-accent)]">pour chaque cliente.</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm font-light leading-relaxed text-[var(--brand-muted)]">
            Chaque prestation débute par un diagnostic personnalisé et se termine par des conseils d'entretien sur mesure.
          </p>
        </Reveal>

        {categories.map((cat, ci) => (
          <div key={cat.name} className="mt-24 first:mt-20">
            <Reveal className="flex items-baseline gap-6">
              <span className="font-display text-6xl leading-none text-[var(--brand-border)] select-none">0{ci + 1}</span>
              <div>
                <h3 className="font-display text-3xl sm:text-4xl text-[var(--brand-dark)]">{cat.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">{CATEGORY_TITLES[cat.name] || ""}</p>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {cat.services.map((s, i) => (
                <Reveal key={s.id} delay={(i % 3) * 0.08}>
                  <article className="group flex h-full flex-col overflow-hidden border border-black/5 bg-[var(--brand-bg)]" data-testid={`service-card-${s.id}`}>
                    <div className="h-52 overflow-hidden">
                      <img src={imgSrc(categoryImages[cat.name])} alt={s.name}
                           className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-8">
                      <h4 className="font-display text-2xl text-[var(--brand-dark)]">{s.name}</h4>
                      <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-[var(--brand-muted)]">{s.description}</p>
                      <div className="mt-6 flex items-center justify-between border-t border-black/5 pt-5">
                        <span className="font-display text-2xl text-[var(--brand-dark)]">{s.price} €</span>
                        <span className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">{formatDuration(s.duration)}</span>
                      </div>
                      <button onClick={() => bookService(s.id)}
                              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand-dark)] px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] text-[var(--brand-bg)] transition-transform duration-300 hover:scale-[0.97]"
                              data-testid={`service-book-btn-${s.id}`}>
                        Prendre rendez-vous <ArrowUpRight size={14} />
                      </button>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
